import {
  FormControl,
  Grid,
  SxProps,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { Controller, FieldError } from 'react-hook-form';

import { gridSize } from '@/shared/constants';
import { Theme } from '@emotion/react';

type CustomTextAreaProps = {
  label: string;
  error: FieldError | undefined;
  helperText: React.ReactNode;
  disabled?: boolean;
  shrink?: boolean;
  required?: boolean;

  type?: React.HTMLInputTypeAttribute;

  size?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };

  register?: TextFieldProps;

  control?: any;
  name?: string;
  defaultValue?: string | number | null;
  onChangeValue?: (value: any) => void;

  sizeTextField?: 'medium' | 'small';
  startAdornment?: React.ReactNode;

  sxTextField?: SxProps<Theme>;

  // text area
  rows?: number;
  minRows?: number;
  maxRows?: number;
  /** Si es true, el textarea crece con el contenido (minRows..maxRows) en vez de altura fija (rows). */
  autoGrow?: boolean;
  /**
   * Mayúscula solo visual, debe transformar a mayúscula previo al request
   */
  typeOfEntry?: 'observacion' | undefined

  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase' | 'full-width' | 'full-size-kana'
};

const CustomTextArea: React.FC<CustomTextAreaProps> = ({
  label,
  error,
  helperText,
  required = true,
  disabled = false,
  shrink = false,
  size = gridSize,
  type = 'text',

  control,
  name,
  defaultValue,
  onChangeValue,
  sizeTextField = 'medium',

  startAdornment,
  sxTextField,

  rows = 2,
  minRows = 1,
  maxRows = 12,
  autoGrow = false,
  typeOfEntry,

  textTransform = "uppercase"
}) => {
  return (
    <Grid item {...size}>
      <FormControl fullWidth variant="outlined">
        <Controller
          control={control}
          name={name!}
          defaultValue={defaultValue ?? ''}
          render={({ field }) => {
            const onChange = (event: any) => {
              const currentValue = event.target.value;

              if (
                type === 'number' ||
                type === 'email' ||
                type === 'password' ||
                type === 'none'
              ) {                
                onChangeValue && onChangeValue(currentValue);
                field.onChange(currentValue);
                return;
              }
              if(typeOfEntry==='observacion'){
                field.onChange(currentValue);
                onChangeValue && onChangeValue(currentValue);
              }else{
                field.onChange(currentValue.toUpperCase());
                onChangeValue && onChangeValue(currentValue?.toUpperCase());
              }
            };

            return (
              <TextField
                {...field}
                size={sizeTextField}
                fullWidth
                variant="outlined"
                label={label}
                InputLabelProps={{
                  ...(shrink && { shrink: true }),
                  ...(startAdornment && { startAdornment }),
                }}

                error={!!error}
                helperText={helperText}
                type={type}
                onChange={onChange}
                required={required}
                inputProps={{ readOnly: disabled, style: { textTransform } }}
                sx={{
                  ...(disabled
                    ? {
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(0, 0, 0, 0.035)",
                        cursor: "not-allowed",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(239, 239, 239, 0.16)",
                          borderStyle: "dashed",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 0, 0, 0.22)",
                        },
                      },
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "rgba(15, 23, 42, 0.88)",
                        color: "rgba(15, 23, 42, 0.88)",
                        fontWeight: 600,
                        cursor: "not-allowed",
                      },
                      "& .MuiInputLabel-root.Mui-disabled": {
                        color: "text.secondary",
                        fontWeight: 500,
                      },
                    }
                    : {
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d3d3d3",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#9395ec",
                        },
                      },
                    }),
                  ...sxTextField,
                }}
                // text area
                autoComplete='off'
                autoCorrect='off'
                multiline
                {...(autoGrow ? (disabled ? { minRows } : { minRows, maxRows }) : { rows })}

              />
            );
          }}
        ></Controller>
      </FormControl>
    </Grid>
  );
};

export default CustomTextArea;
